import { Controller } from 'stimulus'
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js'

import CheckoutController from './checkout_controller'
import { AccountForm } from './form_account_controller'
import { AddressForm } from './form_address_controller'

export type PlansForm = {
  plan: 'yearly' | 'monthly'
  payment_method_id: string
}

export default class FormAddressController extends Controller {
  declare planTarget: HTMLInputElement
  declare promoTarget: HTMLInputElement
  declare yearlyTargets: HTMLElement[]
  declare monthlyTargets: HTMLElement[]
  declare loadingTargets: HTMLElement[]
  declare notLoadingTargets: HTMLElement[]
  declare errorTargets: HTMLElement[]
  static targets = [
    'plan',
    'yearly',
    'monthly',
    'loading',
    'notLoading',
    'error',
  ]

  declare stripePublishableKeyValue: string
  static values = {
    stripePublishableKey: String,
  }

  declare checkoutOutlet: CheckoutController
  static outlets = ['checkout']

  private stripe: Stripe | null = null
  private elements: StripeElements | null = null
  private loading = false

  async connect() {
    if (!this.checkoutOutlet) throw new Error('Missing checkout outlet')
    this.setInitialValues()
    await this.initializeStripe()
    this.checkoutOutlet.onPage3()
  }

  setInitialValues() {
    if (!this.checkoutOutlet.plansForm) return
    const { plan } = this.checkoutOutlet.plansForm

    this.planTarget.value = plan
    this.showPlanTargets(plan)
  }

  setLoading() {
    this.loading = true
    this.loadingTargets.forEach((target) => (target.hidden = false))
    this.notLoadingTargets.forEach((target) => (target.hidden = true))
  }

  setNotLoading() {
    this.loading = false
    this.loadingTargets.forEach((target) => (target.hidden = true))
    this.notLoadingTargets.forEach((target) => (target.hidden = false))
  }

  showErrors() {
    this.errorTargets.forEach((target) => (target.hidden = false))
  }

  hideErrors() {
    this.errorTargets.forEach((target) => (target.hidden = true))
  }

  selectPlan(event: SelectPlanEvent) {
    const { plan } = event.params

    this.planTarget.value = plan
    this.showPlanTargets(plan)
  }

  showPlanTargets(plan: PlansForm['plan']) {
    const targetsToShow =
      plan === 'yearly' ? this.yearlyTargets : this.monthlyTargets

    const targetsToHide =
      plan === 'yearly' ? this.monthlyTargets : this.yearlyTargets

    targetsToShow.forEach((target) => (target.hidden = false))
    targetsToHide.forEach((target) => (target.hidden = true))
  }

  async initializeStripe() {
    const stripe = await loadStripe(this.stripePublishableKeyValue)
    this.stripe = stripe

    const elements = stripe?.elements({
      mode: 'setup',
      currency: 'usd',
      paymentMethodCreation: 'manual',
      appearance: {
        variables: {
          colorPrimary: '#235aff',
          colorBackground: '#e0e7ff',
          colorText: '#111827',
          colorDanger: '#ef4444',
          fontFamily: 'Poppins, system-ui, sans-serif',
          borderRadius: '8px',
        },
      },
    })

    if (!elements) throw new Error('Stripe Elements not available')
    this.elements = elements

    const paymentElement = elements?.create('payment', {
      layout: 'tabs',
      fields: {
        billingDetails: 'never',
      },
    })

    paymentElement?.mount('#payment-element')
  }

  async submit(event: SubmitEvent) {
    if (!event.target) throw new Error('Missing event target')

    // The submitD2c method of the checkout controller will handle the submission,
    // so disable the default form submission behavior
    event.preventDefault()

    if (this.loading) return

    try {
      if (!this.checkoutOutlet.accountForm || !this.checkoutOutlet.addressForm)
        throw new Error('Missing account or address form')

      this.setLoading()

      const paymentMethod = await this.createPaymentMethod(
        this.checkoutOutlet.accountForm,
        this.checkoutOutlet.addressForm,
      )

      const formData = new FormData(event.target as HTMLFormElement)
      const planForm: PlansForm = {
        plan: formData.get('plan') as PlansForm['plan'],
        payment_method_id: paymentMethod.id,
      }

      this.checkoutOutlet.setPlanForm(planForm)
      await this.checkoutOutlet.submitD2c()
    } catch (error) {
      this.setNotLoading()
      this.showErrors()
      throw error
    }
  }

  async createPaymentMethod(
    accountForm: AccountForm,
    addressForm: AddressForm,
  ) {
    if (!this.stripe || !this.elements)
      throw new Error('Stripe not initialized')

    // trigger form validation and wallet collections
    const { error: submitError } = await this.elements.submit()
    if (submitError) throw new Error(submitError.message)

    const { error, paymentMethod } = await this.stripe.createPaymentMethod({
      elements: this.elements,
      params: {
        billing_details: {
          name: `${accountForm.first_name} ${accountForm.last_name}`,
          email: accountForm.email,
          phone: addressForm.phone,
          address: {
            line1: addressForm.address1,
            line2: addressForm.address2 || '',
            city: addressForm.city,
            state: addressForm.state,
            postal_code: addressForm.zip,
            country: 'US',
          },
        },
      },
    })
    if (error) throw new Error(error.message)

    return paymentMethod
  }
}

class SelectPlanEvent extends Event {
  declare params: {
    plan: PlansForm['plan']
  }
}
